import { useEffect, useState } from 'react';
import {
  listOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
} from '../../api/organizations';
import { useAuthStore } from '../../stores/authStore';
import { useOrganizationStore } from '../../stores/organizationStore';
import { canCreateChildOrg } from '../../utils/rbac';
import { OrgForm } from './OrgForm';
import { toast } from '../../stores/uiStore';
import type { Organization } from '../../types/organization';
import styles from './AdminPage.module.css';

type FormMode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; org: Organization };

export function OrgTree() {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<FormMode>({ kind: 'closed' });

  const user = useAuthStore((s) => s.user);
  const adminOrg = orgs.find((o) => o.id === user?.organization_id);
  const adminOrgLevel = adminOrg?.level ?? null;

  const canCreateAny =
    canCreateChildOrg(adminOrgLevel, 'federal') ||
    canCreateChildOrg(adminOrgLevel, 'canton') ||
    canCreateChildOrg(adminOrgLevel, 'commune');

  const setStoreOrganizations = useOrganizationStore((s) => s.setOrganizations);

  useEffect(() => {
    loadOrgs();
  }, []);

  async function loadOrgs() {
    try {
      setLoading(true);
      setError(null);
      const data = await listOrganizations('flat');
      setOrgs(data);
      setStoreOrganizations(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load organizations';
      setError(msg);
      console.error('Failed to load organizations:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(data: {
    name: string;
    code: string;
    level: 'federal' | 'canton' | 'commune';
    parent_id: string | null;
  }) {
    await createOrganization(data);
    toast.success('Organization created.');
    setFormMode({ kind: 'closed' });
    await loadOrgs();
  }

  async function handleEdit(data: {
    name: string;
    code: string;
    level: 'federal' | 'canton' | 'commune';
    parent_id: string | null;
  }) {
    if (formMode.kind !== 'edit') return;
    await updateOrganization(formMode.org.id, { name: data.name });
    toast.success('Organization updated.');
    setFormMode({ kind: 'closed' });
    await loadOrgs();
  }

  async function handleDelete(id: string) {
    if (!confirm('Deactivate this organization?')) return;
    try {
      await deleteOrganization(id);
      toast.success('Organization deactivated.');
      await loadOrgs();
    } catch (err) {
      console.error('Failed to deactivate organization:', err);
      toast.error('Failed to deactivate organization. Please try again.');
    }
  }

  const levelClass: Record<string, string> = {
    federal: styles.federal ?? '',
    canton: styles.canton ?? '',
    commune: styles.commune ?? '',
  };

  if (loading) return <div className={styles.empty}>Loading organizations...</div>;
  if (error) return <div className={styles.empty} style={{ color: 'var(--color-danger, #e74c3c)' }}>{error}</div>;

  return (
    <div>
      {canCreateAny && formMode.kind === 'closed' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button className={styles.createBtn} onClick={() => setFormMode({ kind: 'create' })}>
            + New Organization
          </button>
        </div>
      )}

      {formMode.kind === 'create' && (
        <OrgForm
          organizations={orgs}
          adminOrgLevel={adminOrgLevel}
          onSubmit={handleCreate}
          onCancel={() => setFormMode({ kind: 'closed' })}
        />
      )}

      {formMode.kind === 'edit' && (
        <OrgForm
          organization={formMode.org}
          organizations={orgs}
          adminOrgLevel={adminOrgLevel}
          onSubmit={handleEdit}
          onCancel={() => setFormMode({ kind: 'closed' })}
        />
      )}

      {orgs.length === 0 ? (
        <div className={styles.empty}>No organizations yet. Create your first one.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Level</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((org) => (
              <tr key={org.id}>
                <td style={{ paddingLeft: org.level === 'canton' ? 20 : org.level === 'commune' ? 40 : 0 }}>
                  {org.name}
                </td>
                <td>{org.code}</td>
                <td>
                  <span className={`${styles.levelBadge} ${levelClass[org.level] ?? ''}`}>
                    {org.level}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className={styles.actionBtn}
                      onClick={() => setFormMode({ kind: 'edit', org })}
                    >
                      Edit
                    </button>
                    <button
                      className={`${styles.actionBtn} ${styles.dangerBtn}`}
                      onClick={() => handleDelete(org.id)}
                    >
                      Deactivate
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
